// src/pages/Gas.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const EXCLUDED_USERS = [
  'David Cerquetelli',
  'Da Assegnare',
  'Vincenzo Morresi',
];


const API_BASE = '/api';

function toNumberOrZero(value) {
  if (value === '' || value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

// Nome per visualizzare un qualsiasi utente (non quello corrente, per il quale useremo un campo dedicato)
function getUserDisplayName(user) {
  if (!user) return 'Utente';

  const {
    fullName,
    nome,
    cognome,
    firstName,
    lastName,
    username,
    email,
  } = user;

  // 1) Nome + Cognome / firstName + lastName
  const nameParts = [
    nome || firstName || '',
    cognome || lastName || '',
  ].filter(Boolean);

  if (nameParts.length) {
    return nameParts.join(' ');
  }

  // 2) fullName ripulito da eventuali email dentro
  if (fullName) {
    const cleanParts = fullName
      .split(/\s+/)
      .filter((p) => p && !p.includes('@'));
    if (cleanParts.length) return cleanParts.join(' ');
  }

  // 3) username, ma solo se NON è una mail
  if (username && !username.includes('@')) {
    return username;
  }

  // 4) parte locale dell'email (prima della @), con punto → spazio
  if (email && email.includes('@')) {
    const local = email.split('@')[0];
    if (local) return local.replace('.', ' ');
  }

  return 'Utente';
}

// Data/ora in formato: 17/11/2025 alle ore 22:41
function formatNow() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} alle ore ${hh}:${min}`;
}

// Ricava il nome *vero* dell'utente loggato interrogando /users
async function resolveCurrentUserDisplayName(basicUser) {
  const userId = basicUser?.id || basicUser?._id;
  if (!userId) return getUserDisplayName(basicUser);

  try {
    // Prendiamo tutti gli utenti e troviamo il nostro per id
    const res = await authFetch(`${API_BASE}/users`);
    if (res.ok) {
      const allUsers = await res.json();
      const fullUser =
        allUsers.find(
          (u) => u._id === userId || u.id === userId
        ) || basicUser;
      return getUserDisplayName(fullUser);
    }
  } catch (e) {
    console.error('Errore nel recupero nome completo utente:', e);
  }

  // fallback: anche se non troviamo niente, almeno niente "Utente"
  const fallback = getUserDisplayName(basicUser);
  return fallback === 'Utente' ? 'Senza nome' : fallback;
}

export default function Gas() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserDisplayName, setCurrentUserDisplayName] = useState(''); // <- QUI
  const [role, setRole] = useState(localStorage.getItem('role') || '');
  const [loading, setLoading] = useState(true);

  const [levels, setLevels] = useState([]); // lista di documenti gas
  const [editingValues, setEditingValues] = useState({});
  const [newGasName, setNewGasName] = useState('');
  const [newGasQty, setNewGasQty] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [incomingRequests, setIncomingRequests] = useState([]);

  const [transferModal, setTransferModal] = useState({
    open: false,
    gasId: null,
    gasName: '',
    amount: '',
    targetUserId: '',
  });

  const [requestModal, setRequestModal] = useState({
    open: false,
    gasName: '',
    toUserId: '',
    toUserLabel: '',
    amount: '',
  });

  const getFullName = (user) => {
    if (!user) return '';
    return `${user.nome || ''} ${user.cognome || ''}`.trim();
  };
  

  const allowedRoles = ['admin', 'gestione', 'tecnico', 'segreteria'];
  const currentUserId = currentUser?.id || currentUser?._id;

  // === helper notifiche ===
  const sendGasNotification = async (type, message) => {
    try {
      await authFetch(`${API_BASE}/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          message,
          roles: ['admin', 'gestione', 'segreteria'],
        }),
      });
    } catch (err) {
      console.error('Errore invio notifica gas:', err);
    }
  };

  // === caricamento iniziale ===
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      setInfo('');

      try {
        // 1) Utente corrente
        const userRes = await authFetch(`${API_BASE}/user`);
        if (!userRes.ok) {
          if (userRes.status === 401) {
            throw new Error('Non sei autenticato. Effettua il login.');
          }
          throw new Error('Impossibile recuperare i dati utente');
        }
        const userData = await userRes.json();
        setCurrentUser(userData);
        const userRole = (userData.role || '').toString().toLowerCase();
        setRole(userRole);

        // Ricaviamo SUBITO il nome bello dell’utente
        const displayName = await resolveCurrentUserDisplayName(userData);
        setCurrentUserDisplayName(displayName);

        if (!allowedRoles.includes(userRole)) {
          setLevels([]);
          setLoading(false);
          return;
        }

        // 2) Livelli gas
        const gasRes = await authFetch(`${API_BASE}/gas`);
        if (!gasRes.ok) {
          const data = await gasRes.json().catch(() => null);
          throw new Error(data?.error || 'Errore nel recupero dei livelli gas');
        }
        let gasData = await gasRes.json();

        const hasOwnGas = gasData.some(
          (g) =>
            g.user &&
            (g.user.id === userData.id || g.user._id === userData.id)
        );
        if (!hasOwnGas) {
          const initRes = await authFetch(`${API_BASE}/gas/init-defaults`, {
            method: 'POST',
          });
          if (initRes.ok) {
            const ownGas = await initRes.json();
            gasData = [...gasData, ...ownGas];
          }
        }

        const currentId = userData.id;
        gasData.sort((a, b) => {
          const aUserId = a.user?._id || a.user?.id || '';
          const bUserId = b.user?._id || b.user?.id || '';

          const aIsOwn = aUserId === currentId;
          const bIsOwn = bUserId === currentId;
          if (aIsOwn && !bIsOwn) return -1;
          if (!aIsOwn && bIsOwn) return 1;

          const aName = getUserDisplayName(a.user).toLowerCase();
          const bName = getUserDisplayName(b.user).toLowerCase();
          if (aName < bName) return -1;
          if (aName > bName) return 1;

          return a.gasName.toLowerCase() < b.gasName.toLowerCase() ? -1 : 1;
        });

        setLevels(gasData);

        const initialEditing = {};
        gasData.forEach((g) => {
          initialEditing[g._id] = g.quantityGrams ?? 0;
        });
        setEditingValues(initialEditing);

        // 3) Richieste in arrivo
        try {
          const reqRes = await authFetch(`${API_BASE}/gas/requests?target=me`);
          if (reqRes.ok) {
            const reqData = await reqRes.json();
            setIncomingRequests(reqData);
          }
        } catch {
          // non bloccare se fallisce
        }
      } catch (err) {
        console.error('Errore caricamento Gas:', err);
        setError(err.message || 'Errore durante il caricamento dei livelli gas.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const isRoleAllowed = allowedRoles.includes(role);

  const isSecretariat = role === 'segreteria';
  const canWrite = !isSecretariat;


  // === grouping per furgone ===
  const groupedByUser = useMemo(
    () =>
      levels.reduce((acc, g) => {
        if (!g.user) return acc;

        // ❌ la segreteria non possiede un furgone: non mostrarla nella lista
        if ((g.user.role || '').toString().toLowerCase() === 'segreteria') {
          return acc;
        }


        const fullName = getFullName(g.user);

        // ❌ escludo utenti per nome+cognome
        if (EXCLUDED_USERS.includes(fullName)) {
          return acc;
        }

        const key = g.user._id || g.user.id;
        if (!key) return acc;

        if (!acc[key]) {
          acc[key] = {
            user: g.user,
            items: [],
          };
        }

        acc[key].items.push(g);
        return acc;
      }, {}),
    [levels]
  );



  // lista colleghi per il trasferimento
  const transferTargets = useMemo(() => {
    const list = [];
    Object.values(groupedByUser).forEach(({ user }) => {
      const id = user?._id || user?.id;
      if (!id || id === currentUserId) return;
      const label = getUserDisplayName(user);
      list.push({ id, label });
    });

    const uniq = [];
    const seen = new Set();
    for (const u of list) {
      if (!seen.has(u.id)) {
        seen.add(u.id);
        uniq.push(u);
      }
    }
    return uniq;
  }, [groupedByUser, currentUserId]);

  // === handlers quantità proprie ===
  const handleChangeQty = (id, value) => {
    setEditingValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleSaveQty = async (id) => {
    const newValue = toNumberOrZero(editingValues[id]);
    setError('');
    setInfo('');

    const gas = levels.find((g) => g._id === id);
    const oldValue = gas ? toNumberOrZero(gas.quantityGrams) : 0;

    try {
      const res = await authFetch(`${API_BASE}/gas/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantityGrams: newValue }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "Errore nell'aggiornamento del gas");
      }

      const updated = await res.json();
      setLevels((prev) =>
        prev.map((g) => (g._id === updated._id ? updated : g))
      );
      setInfo('Quantità aggiornata correttamente.');

      // NOTIFICA ACQUISTO / MODIFICA
      if (gas) {
        const delta = newValue - oldValue;
        const displayName =
          currentUserDisplayName || getUserDisplayName(currentUser);
        const when = formatNow();
        let message;

        if (delta > 0) {
          message = `${displayName} ha acquistato ${delta} g di ${gas.gasName} il ${when}`;
        } else if (delta < 0) {
          message = `${displayName} ha ridotto di ${Math.abs(
            delta
          )} g il livello di ${gas.gasName} (nuovo livello ${newValue} g) il ${when}`;
        } else {
          message = `${displayName} ha confermato il livello di ${gas.gasName} a ${newValue} g il ${when}`;
        }

        await sendGasNotification('gas_update', message);
      }
    } catch (err) {
      console.error('Errore salvataggio quantità gas:', err);
      setError(err.message || "Errore nell'aggiornamento del gas.");
    }
  };

  // === aggiunta nuovo gas ===
  const handleAddGas = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!newGasName.trim()) {
      setError('Inserisci il nome del nuovo gas.');
      return;
    }

    const qty = newGasQty === '' ? 0 : Number(newGasQty);
    if (Number.isNaN(qty) || qty < 0) {
      setError('Quantità non valida per il nuovo gas.');
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/gas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gasName: newGasName.trim(),
          quantityGrams: qty,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nella creazione del nuovo gas');
      }

      const created = await res.json();
      setLevels((prev) => {
        const next = [...prev, created];
        const currentId = currentUserId;
        next.sort((a, b) => {
          const aUserId = a.user?._id || a.user?.id || '';
          const bUserId = b.user?._id || b.user?.id || '';

          const aIsOwn = aUserId === currentId;
          const bIsOwn = bUserId === currentId;
          if (aIsOwn && !bIsOwn) return -1;
          if (!aIsOwn && bIsOwn) return 1;

          const aName = getUserDisplayName(a.user).toLowerCase();
          const bName = getUserDisplayName(b.user).toLowerCase();
          if (aName < bName) return -1;
          if (aName > bName) return 1;

          return a.gasName.toLowerCase() < b.gasName.toLowerCase() ? -1 : 1;
        });
        return next;
      });

      setEditingValues((prev) => ({
        ...prev,
        [created._id]: created.quantityGrams ?? 0,
      }));

      setNewGasName('');
      setNewGasQty('');
      setInfo('Nuovo gas aggiunto correttamente.');

      // NOTIFICA ACQUISTO NUOVO GAS
      const displayName =
        currentUserDisplayName || getUserDisplayName(currentUser);
      const when = formatNow();
      const qtyMsg = created.quantityGrams ?? qty ?? 0;
      const message = `${displayName} ha acquistato ${qtyMsg} g di ${created.gasName} il ${when}`;
      await sendGasNotification('gas_new', message);
    } catch (err) {
      console.error('Errore aggiunta gas:', err);
      setError(err.message || 'Errore nella creazione del nuovo gas.');
    }
  };

  // === popup TRASFERISCI ===
  const openTransferModal = (gas) => {
    setTransferModal({
      open: true,
      gasId: gas._id,
      gasName: gas.gasName,
      amount: '',
      targetUserId: '',
    });
    setError('');
    setInfo('');
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    const amount = Number(transferModal.amount);
    if (!transferModal.targetUserId) {
      setError('Seleziona un destinatario.');
      return;
    }
    if (Number.isNaN(amount) || amount <= 0) {
      setError('Inserisci una quantità valida da trasferire.');
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/gas/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceGasId: transferModal.gasId,
          targetUserId: transferModal.targetUserId,
          amountGrams: amount,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore durante il trasferimento gas');
      }

      const { source, target } = await res.json();
      setLevels((prev) => {
        let next = prev.map((g) => {
          if (g._id === source._id) return source;
          if (g._id === target._id) return target;
          return g;
        });

        const hasTarget = next.some((g) => g._id === target._id);
        if (!hasTarget) next = [...next, target];

        const currentId = currentUserId;
        next.sort((a, b) => {
          const aUserId = a.user?._id || a.user?.id || '';
          const bUserId = b.user?._id || b.user?.id || '';

          const aIsOwn = aUserId === currentId;
          const bIsOwn = bUserId === currentId;
          if (aIsOwn && !bIsOwn) return -1;
          if (!aIsOwn && bIsOwn) return 1;

          const aName = getUserDisplayName(a.user).toLowerCase();
          const bName = getUserDisplayName(b.user).toLowerCase();
          if (aName < bName) return -1;
          if (aName > bName) return 1;

          return a.gasName.toLowerCase() < b.gasName.toLowerCase()
            ? -1
            : 1;
        });

        return next;
      });

      // NOTIFICA TRASFERIMENTO
      const displayName =
        currentUserDisplayName || getUserDisplayName(currentUser);
      const targetLabel =
        transferTargets.find((u) => u.id === transferModal.targetUserId)
          ?.label || 'un collega';
      const when = formatNow();
      const message = `${displayName} ha trasferito ${amount} g di ${transferModal.gasName} a ${targetLabel} il ${when}`;
      await sendGasNotification('gas_transfer', message);

      setTransferModal({
        open: false,
        gasId: null,
        gasName: '',
        amount: '',
        targetUserId: '',
      });
      setInfo('Trasferimento registrato con successo.');
    } catch (err) {
      console.error('Errore trasferimento gas:', err);
      setError(err.message || 'Errore durante il trasferimento gas.');
    }
  };

  // === popup RICHIEDI ===
  const openRequestModal = (gas, ownerUser) => {
    const toUserId = ownerUser?._id || ownerUser?.id;
    const label = getUserDisplayName(ownerUser);

    setRequestModal({
      open: true,
      gasName: gas.gasName,
      toUserId,
      toUserLabel: label,
      amount: '',
    });
    setError('');
    setInfo('');
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    const amount = Number(requestModal.amount);
    if (!requestModal.toUserId) {
      setError('Destinatario non valido.');
      return;
    }
    if (Number.isNaN(amount) || amount <= 0) {
      setError('Inserisci una quantità valida da richiedere.');
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/gas/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          toUserId: requestModal.toUserId,
          gasName: requestModal.gasName,
          amountGrams: amount,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nella creazione della richiesta'
        );
      }

      await res.json();

      // NOTIFICA RICHIESTA
      const displayName =
        currentUserDisplayName || getUserDisplayName(currentUser);
      const when = formatNow();
      const message = `${displayName} ha richiesto ${amount} g di ${requestModal.gasName} a ${requestModal.toUserLabel} il ${when}`;
      await sendGasNotification('gas_request', message);

      setRequestModal({
        open: false,
        gasName: '',
        toUserId: '',
        toUserLabel: '',
        amount: '',
      });
      setInfo('Richiesta inviata correttamente.');
    } catch (err) {
      console.error('Errore richiesta gas:', err);
      setError(err.message || 'Errore nella creazione della richiesta.');
    }
  };

  // === richieste in arrivo: accetta / rifiuta ===
  const handleIncomingRequestAction = async (reqId, action) => {
    setError('');
    setInfo('');

    try {
      const url =
        action === 'accept'
          ? `${API_BASE}/gas/requests/${reqId}/accept`
          : `${API_BASE}/gas/requests/${reqId}/reject`;

      const res = await authFetch(url, { method: 'POST' });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore durante la gestione della richiesta'
        );
      }

      const data = await res.json();

      if (action === 'accept') {
        const { source, target } = data;
        setLevels((prev) => {
          let next = prev.map((g) => {
            if (g._id === source._id) return source;
            if (g._id === target._id) return target;
            return g;
          });

          const hasTarget = next.some((g) => g._id === target._id);
          if (!hasTarget) next = [...next, target];

          const currentId = currentUserId;
          next.sort((a, b) => {
            const aUserId = a.user?._id || a.user?.id || '';
            const bUserId = b.user?._id || b.user?.id || '';

            const aIsOwn = aUserId === currentId;
            const bIsOwn = bUserId === currentId;
            if (aIsOwn && !bIsOwn) return -1;
            if (!aIsOwn && bIsOwn) return 1;

            const aName = getUserDisplayName(a.user).toLowerCase();
            const bName = getUserDisplayName(b.user).toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;

            return a.gasName.toLowerCase() < b.gasName.toLowerCase()
              ? -1
              : 1;
          });

          return next;
        });
      }

      setIncomingRequests((prev) => prev.filter((r) => r._id !== reqId));

      setInfo(
        action === 'accept'
          ? 'Richiesta accettata e livelli aggiornati.'
          : 'Richiesta rifiutata.'
      );
    } catch (err) {
      console.error('Errore gestione richiesta gas:', err);
      setError(err.message || 'Errore nella gestione della richiesta.');
    }
  };

  // blocco scroll quando modali aperte
  useEffect(() => {
    const anyOpen = transferModal.open || requestModal.open;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
  }, [transferModal.open, requestModal.open]);

  // === rendering ===
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600 text-lg animate-pulse">
          Caricamento livelli gas...
        </p>
      </div>
    );
  }

  if (!isRoleAllowed) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <Card className="shadow-sm p-6 max-w-lg">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Accesso non disponibile
          </h1>
          <p className="text-sm text-gray-600">
            La gestione dei livelli gas è riservata a utenti con ruolo{' '}
            <span className="font-medium">Admin</span>,{' '}
            <span className="font-medium">Gestione</span> o{' '}
            <span className="font-medium">Tecnico</span>.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-5xl mx-auto px-4 pt-8 space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
            Livelli gas furgone
          </h1>
          <p className="text-sm text-gray-600">
            Ogni utente può modificare solo i livelli del proprio furgone.
            Per i furgoni altrui puoi inviare una richiesta di gas; chi riceve
            può accettare o rifiutare e, in caso di accettazione, i livelli
            vengono aggiornati automaticamente.
          </p>
        </div>

      {canWrite && (
        <>
        {/* Richieste in arrivo */}
        <Card className="shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Richieste di gas in arrivo
          </h2>
          {incomingRequests.length === 0 ? (
            <p className="text-xs text-gray-500">
              Non hai richieste pendenti da altri utenti.
            </p>
          ) : (
            <div className="space-y-2 text-xs">
              {incomingRequests.map((r) => (
                <div
                  key={r._id}
                  className="border border-blue-100 bg-blue-50 rounded-lg px-3 py-2 flex flex-col gap-1"
                >
                  <div className="text-blue-900">
                    <span className="font-semibold">
                      {getUserDisplayName(r.fromUser)}
                    </span>{' '}
                    chiede{' '}
                    <span className="font-semibold">
                      {r.amountGrams} g
                    </span>{' '}
                    di <span className="font-semibold">{r.gasName}</span>.
                  </div>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() =>
                        handleIncomingRequestAction(r._id, 'accept')
                      }
                      className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-[11px] font-semibold"
                    >
                      Accetta
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleIncomingRequestAction(r._id, 'reject')
                      }
                      className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-[11px] font-semibold"
                    >
                      Rifiuta
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        </>
      )}

        {role === 'admin' && (
          <div className="mb-3 flex gap-2">
            <button
              type="button"
              onClick={async () => {
                setError('');
                setInfo('');
                try {
                  const res = await authFetch(`${API_BASE}/gas/init-defaults-all`, {
                    method: 'POST',
                  });
                  const data = await res.json().catch(() => null);
                
                  if (!res.ok) {
                    throw new Error(data?.error || 'Errore inizializzazione furgoni');
                  }
                
                  setInfo(
                    `Inizializzazione completata: ${data?.created ?? 0} record creati su ${data?.users ?? 0} utenti.`
                  );
                
                  // ricarico i livelli
                  const gasRes = await authFetch(`${API_BASE}/gas`);
                  const gasData = gasRes.ok ? await gasRes.json() : [];
                  setLevels(gasData);
                } catch (e) {
                  setError(e.message || 'Errore inizializzazione furgoni');
                }
              }}
              className="px-3 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800"
            >
              Inizializza tutti i furgoni (admin/gestione/tecnico)
            </button>
          </div>
        )}


        {/* Situazione gas per furgone */}
        <Card className="shadow-sm p-4 overflow-x-auto">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Situazione gas per furgone
          </h2>

          {levels.length === 0 ? (
            <p className="text-sm text-gray-500">
              Non ci sono ancora dati sui livelli gas.
            </p>
          ) : (
            <div className="space-y-4">
              {Object.values(groupedByUser).map(({ user, items }) => {
                const userId = user?._id || user?.id;
                const label = getUserDisplayName(user);
                const isOwnUser = userId === currentUserId;

                return (
                  <div
                    key={userId || label}
                    className="border border-gray-200 rounded-xl p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800 text-sm">
                        {label}
                      </span>
                      {isOwnUser && (
                        <span className="text-[11px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                          Il tuo furgone
                        </span>
                      )}
                    </div>
                    <table className="w-full text-xs md:text-sm">
                      <thead>
                        <tr className="text-left text-gray-600 border-b">
                          <th className="py-1 pr-2">Gas</th>
                          <th className="py-1 pr-2">Quantità (g)</th>
                          <th className="py-1 text-right">Azione</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((g) => {
                          const isOwn =
                            g.user?._id === currentUserId ||
                            g.user?.id === currentUserId;

                          return (
                            <tr key={g._id} className="border-b last:border-0">
                              <td className="py-1 pr-2">{g.gasName}</td>
                              <td className="py-1 pr-2">
                                {isOwn ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={
                                      editingValues[g._id] ??
                                      g.quantityGrams ??
                                      0
                                    }
                                    onChange={(e) =>
                                      handleChangeQty(g._id, e.target.value)
                                    }
                                    className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                                  />
                                ) : (
                                  <span>{g.quantityGrams ?? 0}</span>
                                )}
                              </td>
                            <td className="py-1 text-right space-x-1">
                              {canWrite ? (
                                isOwn ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleSaveQty(g._id)}
                                      className="px-2 py-1 rounded-lg text-[11px] font-semibold text-white bg-sky-600 hover:bg-sky-700"
                                    >
                                      Salva
                                    </button>
                                    {transferTargets.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => openTransferModal(g)}
                                        className="px-2 py-1 rounded-lg text-[11px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100"
                                      >
                                        Trasferisci
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openRequestModal(g, user)}
                                    className="px-2 py-1 rounded-lg text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100"
                                  >
                                    Richiedi
                                  </button>
                                )
                              ) : (
                                <span className="text-gray-400 text-[11px]">—</span>
                              )}
                            </td>
                            
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

      {canWrite && (
        <>
        {/* Aggiungi nuovo gas */}
        <Card className="shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Aggiungi nuovo gas
          </h2>
          <p className="text-xs text-gray-500 mb-3">
            Usa questo form per aggiungere un nuovo tipo di gas al tuo
            furgone, ad esempio quando viene acquistata una nuova bombola.
          </p>

          <form onSubmit={handleAddGas} className="space-y-3 text-sm">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nome gas
              </label>
              <input
                type="text"
                value={newGasName}
                onChange={(e) => setNewGasName(e.target.value)}
                placeholder="Es. R32, R744..."
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Quantità iniziale (g)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={newGasQty}
                onChange={(e) => setNewGasQty(e.target.value)}
                placeholder="Es. 1000"
                className="mt-1 w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <button
              type="submit"
              className="mt-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 text-sm font-semibold transition"
            >
              Aggiungi gas al tuo furgone
            </button>
          </form>

          {(error || info) && (
            <div className="mt-3 space-y-2 text-xs">
              {error && (
                <p className="text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              {info && (
                <p className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  {info}
                </p>
              )}
            </div>
          )}
        </Card>
              </>)}
      </div>

      {/* MODAL TRASFERIMENTO */}
      {transferModal.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div
            className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Trasferisci gas a un collega
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              Gas selezionato:{' '}
              <span className="font-semibold">{transferModal.gasName}</span>
            </p>
            <form onSubmit={handleTransferSubmit} className="space-y-3 text-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Destinatario
                </label>
                <select
                  value={transferModal.targetUserId}
                  onChange={(e) =>
                    setTransferModal((prev) => ({
                      ...prev,
                      targetUserId: e.target.value,
                    }))
                  }
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">Seleziona un utente...</option>
                  {transferTargets.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Quantità da trasferire (g)
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={transferModal.amount}
                  onChange={(e) =>
                    setTransferModal((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  className="mt-1 w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() =>
                    setTransferModal({
                      open: false,
                      gasId: null,
                      gasName: '',
                      amount: '',
                      targetUserId: '',
                    })
                  }
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 text-sm font-semibold"
                >
                  Conferma
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL RICHIESTA */}
      {requestModal.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div
            className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Richiedi gas a un collega
            </h2>
            <p className="text-xs text-gray-500 mb-1">
              A:{' '}
              <span className="font-semibold">
                {requestModal.toUserLabel}
              </span>
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Gas richiesto:{' '}
              <span className="font-semibold">{requestModal.gasName}</span>
            </p>
            <form onSubmit={handleRequestSubmit} className="space-y-3 text-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Quantità da richiedere (g)
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={requestModal.amount}
                  onChange={(e) =>
                    setRequestModal((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  className="mt-1 w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() =>
                    setRequestModal({
                      open: false,
                      gasName: '',
                      toUserId: '',
                      toUserLabel: '',
                      amount: '',
                    })
                  }
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-semibold"
                >
                  Invia richiesta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
